import React from "react";
import { isUkCountry } from "../utils/ukTaxUi.js";
import {
  resolveKnownClassificationTaxTreatment,
  resolveRowSemanticTaxTreatment,
} from "../utils/taxTreatmentClassification.js";

/**
 * Normalisiert mögliche Backend-Werte auf:
 * - "income"
 * - "disposal"
 * - null
 */
function normalizeExplicitTreatment(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    normalized === "income" ||
    normalized === "taxable_income" ||
    normalized === "other_income" ||
    normalized === "misc_income" ||
    normalized === "miscellaneous_income"
  ) {
    return "income";
  }

  if (
    normalized === "disposal" ||
    normalized === "capital_gain" ||
    normalized === "capital_gains" ||
    normalized === "sale" ||
    normalized === "trade"
  ) {
    return "disposal";
  }

  return null;
}

function normalizeClassification(row = {}) {
  return String(
    row.classification ||
      row.type ||
      row.event ||
      row.kind ||
      row.action ||
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function getRowToken(row = {}) {
  return String(
    row.token || row.token_symbol || row.tokenSymbol || row.asset || ""
  )
    .trim()
    .toUpperCase();
}

function isJuicyDebtTokenRow(row = {}) {
  const token = getRowToken(row);
  const role = String(row.juicyfinanceRole || "").trim().toLowerCase();
  return role === "debt" || token.startsWith("JUICY_DEBT:");
}

function isJuicySuppliedTokenRow(row = {}) {
  const token = getRowToken(row);
  const role = String(row.juicyfinanceRole || "").trim().toLowerCase();
  return role === "receipt" || token.startsWith("JUICY_SUPPLIED:");
}

function isJuicyUnderlyingRow(row = {}) {
  const role = String(row.juicyfinanceRole || "").trim().toLowerCase();
  if (role === "underlying") return true;
  if (role === "receipt" || role === "debt") return false;
  if (isJuicyDebtTokenRow(row) || isJuicySuppliedTokenRow(row)) return false;
  return true;
}

function isJuicyBorrowClass(cls = "") {
  return /^juicyfinance_[a-z0-9]+_borrow$/.test(cls);
}

function isJuicyRepayClass(cls = "") {
  return /^juicyfinance_[a-z0-9]+_repay$/.test(cls);
}

function isJuicyDepositClass(cls = "") {
  return /^juicyfinance_[a-z0-9]+_deposit$/.test(cls);
}

function isJuicyWithdrawalClass(cls = "") {
  return /^juicyfinance_[a-z0-9]+_withdrawal$/.test(cls);
}

function isUkJuicyDefiCapitalDisposalClass(cls = "") {
  return /^juicyfinance_[a-z0-9]+_(repay|deposit|withdrawal)$/.test(cls);
}

const UK_EXPORT_TOKEN_DISPOSAL_TYPES = new Set([
  "swap_out",
  "nfbc_purchase",
  "v3tax_purchase",
  "mugshot_pro_subscription",
  "vewin_4ocean_donation",
  "one_million_pixel_purchase",
  "golden_empire_spend",
  "golden_empire_burn_spend",
  "vaiyans_vco_game_entry",
  "velottery_ticket_purchase",
  "scoopup_puppy_pass_fee",
  "squad_game_spend",
  "squad_vtho_game_entry",
  "squad_nft_upgrade_spend",
  "squad_preico_payment",
  "juicyfinance_liquidation_debt_payment",
  "gas_fee",
  "gas_abstraction_fee",
]);

const UK_INCOME_CLASSES = new Set([
  "x2earn_reward",
  "x2earn_reward_claim",
  "governance_reward",
  "vestation_reward_claim",
  "staking_reward",
  "stargate_staking_rewards",
  "thorhead_staking_rewards",
  "vemugs_staking_rewards",
  "vemugs_mint_reward",
  "vemugs_reward_distribution",
  "wov_staking_rewards",
  "generated",
  "verocket_generated_vtho_claim",
  "node_legacy_rewards",
  "foundation_bonus",
  "foundation_bonus_or_swap",
  "juicyfinance_b3tr_reward_claim",
  "juicyfinance_lending_income",
  "airdrop",
  "solarwise_electricity_revenue",
]);

function treatmentFromUkMethod(row = {}) {
  const method = String(row.ukMethod || "").trim().toLowerCase();
  if (!method) return null;
  if (method.includes("income")) return "income";
  if (
    method.includes("acquisition") ||
    method.includes("neutral") ||
    method.includes("review") ||
    method.includes("info_only") ||
    method.includes("non_reportable") ||
    method.includes("pool_adjustment")
  ) {
    return null;
  }
  if (method.includes("disposal") || method.includes("capital")) {
    return "disposal";
  }
  return null;
}

function treatmentFromUkRowFlags(row = {}) {
  if (row.isIncome === true || row.direction === "income") return "income";
  if (row.isDisposal === true || row.direction === "disposal") return "disposal";
  if (
    row.isAcquisition === true ||
    row.isNeutral === true ||
    row.direction === "acquisition" ||
    row.direction === "neutral"
  ) {
    return null;
  }
  return undefined;
}

function isGenericUkIncomeClass(cls = "") {
  if (!cls) return false;
  if (UK_EXPORT_TOKEN_DISPOSAL_TYPES.has(cls) || isUkJuicyDefiCapitalDisposalClass(cls)) {
    return false;
  }
  if (isJuicyBorrowClass(cls)) return false;
  return (
    cls.includes("reward") ||
    cls.includes("staking") ||
    cls.includes("income") ||
    cls.endsWith("_claim") ||
    cls.includes("airdrop") ||
    cls.includes("distribution")
  );
}

function resolveUkJuicyFinanceTreatment(row = {}) {
  const cls = normalizeClassification(row);

  if (isJuicyDebtTokenRow(row)) return null;

  if (isJuicyBorrowClass(cls)) return null;

  if (isJuicyRepayClass(cls)) {
    if (row.juicyfinanceUseATokens === true) return null;
    if (isJuicyUnderlyingRow(row)) return "disposal";
    return null;
  }

  if (isJuicyDepositClass(cls)) {
    if (isJuicyUnderlyingRow(row)) return "disposal";
    return null;
  }

  if (isJuicyWithdrawalClass(cls)) {
    if (isJuicySuppliedTokenRow(row)) return "disposal";
    return null;
  }

  return null;
}

function isUkEngineTokenExportDisposal(cls = "") {
  if (!cls) return false;
  return (
    UK_EXPORT_TOKEN_DISPOSAL_TYPES.has(cls) ||
    isUkJuicyDefiCapitalDisposalClass(cls)
  );
}

function resolveUkTaxTreatment(row = {}) {
  if (row.needsReview === true || row.ukReview) return null;

  const semanticTreatment = resolveRowSemanticTaxTreatment(row);
  if (semanticTreatment !== undefined) return semanticTreatment;

  const knownTreatment = resolveKnownClassificationTaxTreatment(row);
  if (knownTreatment) return knownTreatment;

  const flagTreatment = treatmentFromUkRowFlags(row);
  if (flagTreatment === "income" || flagTreatment === "disposal") {
    return flagTreatment;
  }
  if (flagTreatment === null) return null;

  const methodTreatment = treatmentFromUkMethod(row);
  if (methodTreatment) return methodTreatment;

  const juicyTreatment = resolveUkJuicyFinanceTreatment(row);
  if (juicyTreatment) return juicyTreatment;

  const cls = normalizeClassification(row);

  if (isJuicyDebtTokenRow(row) || isJuicyBorrowClass(cls)) return null;

  if (UK_INCOME_CLASSES.has(cls) || cls === "airdrop" || isGenericUkIncomeClass(cls)) {
    return "income";
  }

  if (isUkEngineTokenExportDisposal(cls)) {
    if (isJuicyRepayClass(cls) || isJuicyDepositClass(cls) || isJuicyWithdrawalClass(cls)) {
      return null;
    }
    return "disposal";
  }

  return null;
}

function resolveDefaultTaxTreatment(row = {}, fallback = null) {
  const normalizedFallback = normalizeExplicitTreatment(fallback);
  if (normalizedFallback) return normalizedFallback;

  const semanticTreatment = resolveRowSemanticTaxTreatment(row);
  if (semanticTreatment !== undefined) return semanticTreatment;

  const knownTreatment = resolveKnownClassificationTaxTreatment(row);
  if (knownTreatment) return knownTreatment;

  const cls = normalizeClassification(row);

  const fifoLegs = Array.isArray(row.fifoLegs)
    ? row.fifoLegs
    : Array.isArray(row.matchedInflows)
      ? row.matchedInflows
      : [];

  const explicitDisposal =
    cls === "swap_out" ||
    cls === "nfbc_purchase" ||
    cls === "v3tax_purchase" ||
    cls === "mugshot_pro_subscription" ||
    cls === "vewin_4ocean_donation" ||
    cls === "one_million_pixel_purchase" ||
    cls === "golden_empire_spend" ||
    cls === "golden_empire_burn_spend" ||
    cls === "vaiyans_vco_game_entry" ||
    cls === "velottery_ticket_purchase" ||
    cls === "scoopup_puppy_pass_fee" ||
    cls === "squad_preico_payment" ||
    cls === "juicyfinance_liquidation_debt_payment" ||
    cls === "sale" ||
    cls === "sale_payment" ||
    cls === "mint_payment" ||
    cls === "liquidity_add_out" ||
    cls === "liquidity_remove_lp_out" ||
    cls.includes("swap_out") ||
    cls.includes("sale_payment");

  const hasDisposalShape =
    fifoLegs.length > 0 ||
    row.grossBuyFiat != null ||
    row.grossBuyEUR != null ||
    row.grossSellFiat != null ||
    row.grossSellEUR != null ||
    row.grossProfitFiat != null ||
    row.grossProfitEUR != null;

  if (explicitDisposal || hasDisposalShape) {
    return "disposal";
  }

  const explicitIncome =
    cls === "generated" ||
    cls === "airdrop" ||
    cls === "foundation_bonus" ||
    cls === "verocket_generated_vtho_claim" ||
    cls === "node_legacy_rewards" ||
    cls.includes("x2earn") ||
    cls.includes("reward") ||
    cls.includes("staking") ||
    cls.includes("airdrop") ||
    cls.includes("income") ||
    cls.includes("revenue") ||
    cls.includes("claim") ||
    cls.includes("distribution");

  if (explicitIncome) {
    return "income";
  }

  return null;
}

/**
 * Frontend-Fallback zur Bestimmung der steuerlichen Ergebnisart.
 *
 * Priorität:
 * 1. Explizites Backend-Feld
 * 2. UK-spezifische Logik (inkl. JuicyFinance-Rollen)
 * 3. Default-Heuristik für DE/US/IT
 */
export function resolveTaxTreatment(
  row = {},
  { fallback = null, selectedCountry = "DE" } = {}
) {
  const explicit =
    normalizeExplicitTreatment(row.taxTreatment) ||
    normalizeExplicitTreatment(row.tax_treatment) ||
    normalizeExplicitTreatment(row.taxResultType) ||
    normalizeExplicitTreatment(row.tax_result_type) ||
    normalizeExplicitTreatment(row.resultCategory) ||
    normalizeExplicitTreatment(row.result_category);

  if (explicit) return explicit;

  if (isUkCountry(selectedCountry)) {
    const ukTreatment = resolveUkTaxTreatment(row);
    if (ukTreatment) return ukTreatment;
    return null;
  }

  return resolveDefaultTaxTreatment(row, fallback);
}

const TREATMENT_META = {
  income: {
    de: "Einkünfte",
    en: "Income",

    dot: "bg-emerald-500",
    text: "text-emerald-700",

    surface:
      "bg-emerald-50/65 ring-1 ring-inset ring-emerald-200/45",

    hover:
      "group-hover:bg-emerald-50 " +
      "group-hover:ring-emerald-200/80 " +
      "group-hover:text-emerald-800",

    print:
      "print:bg-transparent print:ring-0 print:text-emerald-700",
  },

  disposal: {
    de: "Veräußerung",
    en: "Disposal",

    dot: "bg-blue-500",
    text: "text-blue-700",

    surface:
      "bg-blue-50/65 ring-1 ring-inset ring-blue-200/45",

    hover:
      "group-hover:bg-blue-50 " +
      "group-hover:ring-blue-200/80 " +
      "group-hover:text-blue-800",

    print:
      "print:bg-transparent print:ring-0 print:text-blue-700",
  },
};

export default function TaxTreatmentValue({
  row,
  language = "de",
  formattedValue,
  treatment: forcedTreatment = null,
  selectedCountry = "DE",
  align = "center",
  showLabel = true,
  className = "",
}) {
  const treatment =
    normalizeExplicitTreatment(forcedTreatment) ||
    resolveTaxTreatment(row, { selectedCountry });

  const meta = treatment ? TREATMENT_META[treatment] : null;
  const label = meta?.[language] || meta?.en || "";

  const alignClass =
    align === "right"
      ? "items-end text-right"
      : align === "left"
        ? "items-start text-left"
        : "items-center text-center";

  const title =
    treatment === "income"
      ? language === "en"
        ? "Included as taxable income"
        : "Als steuerpflichtige Einkünfte berücksichtigt"
      : language === "en"
        ? "Included as a disposal result"
        : "Als Veräußerungsergebnis berücksichtigt";

  return (
    <div
      className={[
        "inline-flex min-w-0 flex-col justify-center",
        alignClass,
        className,
      ].join(" ")}
    >
      <span className="whitespace-nowrap font-mono leading-none text-slate-900 print:text-black">
        {formattedValue}
      </span>

      {showLabel && meta ? (
        <span
          title={title}
          aria-label={title}
          className={[
            "mt-1.5 inline-flex min-h-[15px] items-center gap-1.5",
            "whitespace-nowrap rounded-md px-1.5 py-[2px]",
            "text-[10.5px] font-medium leading-none tracking-[0.015em]",
            "transition-[background-color,color,box-shadow] duration-150",
            meta.text,
            meta.surface,
            meta.hover,
            meta.print,
          ].join(" ")}
        >
          <span
            aria-hidden="true"
            className={[
              "h-[5px] w-[5px] shrink-0 rounded-full",
              meta.dot,
            ].join(" ")}
          />

          <span>{label}</span>
        </span>
      ) : null}
    </div>
  );
}
